import { Add } from '@mui/icons-material';
import { Box, Button } from '@mui/material';
import { DataGridPremium } from '@mui/x-data-grid-premium';
import { useTranslation } from 'react-i18next';

import { useDataGridAutoHeightToScroll } from '~/components/SolaForceDataGrid/hooks/useDataGridAutoHeightToScroll';
import { useDataGridCRUDActions } from '~/components/SolaForceDataGrid/hooks/useDataGridCRUDActions';
import { SolaForceHeading } from '~/components/SolaForceHeading';
import { useDialogState } from '~/hooks/useDialogState';
import { EmployeeEquipmentTableRow } from '~/scenes/PeopleModule/components/Tabs/EquipmentTab/EquipmentTable/data';
import { NewEquipmentDialog } from '~/scenes/PeopleModule/components/Tabs/EquipmentTab/EquipmentTable/NewEquipmentDialog';
import { useEquipmentTable } from '~/scenes/PeopleModule/components/Tabs/EquipmentTab/EquipmentTable/useEquipmentTable';
import { useEquipmentTableColumns } from '~/scenes/PeopleModule/components/Tabs/EquipmentTab/EquipmentTable/useEquipmentTableColumns';
import { LoadingView } from '~/scenes/PortalViews/components/LoadingView';

export const EquipmentTable = () => {
  const { t } = useTranslation();

  const {
    isLoading,
    dataGridProps: dataProps,
    crudConfig,
    columnsConfig,
    newEquipmentDialog,
  } = useEquipmentTable();

  const columnsProps = useEquipmentTableColumns(columnsConfig);

  const {
    dataGridProps,
    toolbarProps: { addNew },
  } = useDataGridCRUDActions<
    EmployeeEquipmentTableRow,
    EmployeeEquipmentTableRow['id']
  >({
    dataGridProps: {
      ...dataProps,
      ...columnsProps,
    },
    config: crudConfig,
  });

  const { dataGridProps: autoHeightProps, containerProps } =
    useDataGridAutoHeightToScroll({
      rowsCount: dataGridProps.rows.length,
      settings: {
        scrollRowCount: 10,
        rowHeight: 36,
        headerRowHeight: 39,
      },
    });

  const addNewDialog = useDialogState();

  if (isLoading) {
    return <LoadingView />;
  }

  return (
    <Box sx={{ maxWidth: '100%' }}>
      <SolaForceHeading heading={t('laCompanyEquipment')}>
        {addNew.available && (
          <Button
            color="primary"
            startIcon={<Add />}
            onClick={addNewDialog.open}
            disabled={addNew.disabled}
          >
            {t('Add')}
          </Button>
        )}
      </SolaForceHeading>
      <Box {...containerProps}>
        <DataGridPremium
          {...dataGridProps}
          {...autoHeightProps}
          density={'compact'}
          initialState={{
            pinnedColumns: { right: ['approve', 'actions'] },
            columns: {
              columnVisibilityModel: {
                fileSize: false,
              },
            },
          }}
          hideFooter={true}
        />
      </Box>
      {addNewDialog.isOpen && (
        <NewEquipmentDialog
          isOpen={addNewDialog.isOpen}
          onClose={addNewDialog.close}
          {...newEquipmentDialog}
          onAddNewRow={addNew.onAddNewRow}
        />
      )}
    </Box>
  );
};
